import Footer from '@/components/Footer';
import BaseOfferCard from '@/components/purchase/card/BaseOfferCard';
import { purchaseFlowBookingCreationLoadingSelector } from '@/features/loading/loadingSelectors';
import { updateSelectedNonTripOffer } from '@/features/purchase/purchaseActions';
import { nonTripOffersSelector } from '@/features/purchase/purchaseSelectors';
import { useBookingCreation } from '@/hooks/useBookingCreation';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { useTranslation } from '@/i18n';
import { TransTenantSpecific } from '@/i18n/trans/tenantSpecific';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { SearchNonTripOfferDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import {
  Button,
  Card,
  Icons,
  Tooltip,
  Typography,
} from '@packages/webshop-design-system';
import { type FC, useCallback, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

interface NonTripOfferListProps {}

const NonTripOfferList: FC<NonTripOfferListProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isPending = useSelector(purchaseFlowBookingCreationLoadingSelector);
  const { nonTripOffers, selectedNonTripOffer } = useSelector(
    nonTripOffersSelector
  );
  const configuration = useTenantConfiguration();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const { handleCreatingAndFetchingNonTripOfferBooking } = useBookingCreation();

  const getOffersColumns = useCallback(
    (offers: Array<SearchNonTripOfferDTO> | undefined) => {
      if (!offers?.length) {
        return [[], []];
      }

      const sortedOffers = [...offers].sort(
        (a, b) =>
          (a.offerSummary?.minimalPrice?.amount ?? 0) -
          (b.offerSummary?.minimalPrice?.amount ?? 0)
      );

      const firstColumn: Array<SearchNonTripOfferDTO> = [];
      const secondColumn: Array<SearchNonTripOfferDTO> = [];

      sortedOffers.forEach((offer, index) => {
        if (index % 2 === 0) {
          firstColumn.push(offer);
        } else {
          secondColumn.push(offer);
        }
      });

      return [firstColumn, secondColumn];
    },
    []
  );

  useEffect(() => {
    if (!nonTripOffers) {
      navigate('/purchase');
    }
  }, [navigate, nonTripOffers]);

  useEffect(() => {
    if (nonTripOffers) {
      dispatch(updateSelectedNonTripOffer(nonTripOffers[0]));
    }
  }, [dispatch, nonTripOffers]);

  const renderOfferCard = (
    nonTripOffer: SearchNonTripOfferDTO,
    isSelected: boolean
  ) => {
    const admissionOfferPart = nonTripOffer.admissionOfferParts?.[0];
    const condition = admissionOfferPart?.conditions?.[0];
    const minimalPrice = nonTripOffer.offerSummary?.minimalPrice;
    const appliedReduction = admissionOfferPart?.appliedReductions?.[0];
    const hint = admissionOfferPart?.productDescriptiveTexts
      ?.map(
        ({ description, shortDescription }) => description ?? shortDescription
      )
      .filter(Boolean)
      .join('\n');
    return (
      <BaseOfferCard
        key={nonTripOffer.id}
        title={admissionOfferPart?.productDescription ?? ''}
        amount={minimalPrice?.amount || 0}
        isSelected={isSelected}
        onSelect={() => dispatch(updateSelectedNonTripOffer(nonTripOffer))}
        descriptions={condition?.description ? [condition.description] : []}
        isRefundable={admissionOfferPart?.refundable === 'YES'}
        isChangeable={admissionOfferPart?.exchangeable === 'YES'}
        appliedReduction={appliedReduction}
        titleAdornment={
          !!hint?.length && (
            <Tooltip
              data-testid="non-trip-offer-descriptive-texts"
              aria-label={t('text.travelPassDetails')}
              trigger={<Icons.infoCircle className="h-4 w-4 text-primary" />}
              content={hint}
              placement="left bottom"
              className="max-w-[300px] whitespace-pre-wrap"
            />
          )
        }
        data-testid="select-non-trip-offer"
      />
    );
  };

  const [firstColumn, secondColumn] = getOffersColumns(nonTripOffers);

  return (
    <>
      <div className="mx-auto w-full max-w-[800px] p-0 pb-24 laptop:p-4 laptop:pb-24">
        <Typography
          variant={isLaptopOrBigger ? 'heading1' : 'heading2'}
          className="p-4 laptop:p-0 laptop:pb-4"
          as="h1"
        >
          <TransText i18nKey="selectTravelPass" />
        </Typography>

        <>
          <Card
            className={cn(
              'flex flex-col gap-4 rounded-none border-none p-4 shadow-none laptop:flex-none laptop:flex-grow laptop:flex-row laptop:rounded-lg laptop:border-solid laptop:shadow-sm'
            )}
          >
            {isLaptopOrBigger ? (
              <>
                <div className="flex w-full flex-col gap-3">
                  {firstColumn?.map((nonTripOffer) =>
                    renderOfferCard(
                      nonTripOffer,
                      Boolean(selectedNonTripOffer?.id === nonTripOffer.id)
                    )
                  )}
                </div>
                <div className="flex w-full flex-col gap-3">
                  {secondColumn?.map((nonTripOffer) =>
                    renderOfferCard(
                      nonTripOffer,
                      Boolean(selectedNonTripOffer?.id === nonTripOffer.id)
                    )
                  )}
                </div>
              </>
            ) : (
              nonTripOffers?.map((nonTripOffer) =>
                renderOfferCard(
                  nonTripOffer,
                  Boolean(selectedNonTripOffer?.id === nonTripOffer.id)
                )
              )
            )}
          </Card>
          {configuration?.showTravelPassDisclaimer && (
            <div className="prose flex items-center gap-1.5 px-4 pb-2 prose-p:m-0 prose-a:text-blue-600 prose-a:no-underline laptop:px-0 laptop:pb-0 laptop:pt-4">
              <Icons.infoCircle />
              <Typography variant="body2" className="text-center">
                <TransTenantSpecific i18nKey="travelPassDisclaimer" />
              </Typography>
            </div>
          )}
        </>
      </div>
      <Footer
        actionButtons={
          <Button
            size={isLaptopOrBigger ? 'medium' : 'large'}
            data-testid="footer-continue-button"
            className="rounded-lg laptop:w-auto"
            isDisabled={!selectedNonTripOffer}
            onClick={handleCreatingAndFetchingNonTripOfferBooking}
            variant="cta"
            isPending={isPending}
            endAdornment={<Icons.arrowRight />}
          >
            <TransText i18nKey="continue" />
          </Button>
        }
      />
    </>
  );
};

export default NonTripOfferList;
